import { useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, X } from "lucide-react";
import DraggableWrapper from "./DraggableWrapper";

const FeedbackWidget = () => {
  const { language } = useContext(AuthContext);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  if (location.pathname === "/chat") {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/feedback`, 
        { feedback: feedback },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t("¡Gracias por tu feedback!", "Gràcies pel teu feedback!"));
      setFeedback("");
      setIsOpen(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error(t("Error al enviar", "Error en enviar"));
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  return (
    <>
      {!isOpen && (
        <DraggableWrapper initialBottom={16} initialRight={6}>
          <button
            data-testid="feedback-button"
            onClick={() => setIsOpen(true)}
            className="bg-gradient-to-r from-teal-400 to-teal-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all group"
            aria-label="Feedback"
          >
            <MessageSquare className="w-5 h-5" />
          </button>
        </DraggableWrapper>
      )}

      {/* Feedback Panel */}
      {isOpen && (
        <div
          data-testid="feedback-panel"
          className="fixed bottom-16 right-6 w-72 sm:w-80 glass rounded-2xl p-5 shadow-2xl border border-teal-400/20 z-40 fade-in"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-teal-300" />
              <h3 className="text-lg font-semibold text-teal-200">
                {t("Tu Feedback", "El Teu Feedback")}
              </h3>
            </div>
            <button
              data-testid="close-feedback-btn"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-gray-300 text-sm mb-4">
            {t(
              "¿Qué te parece ESSENC·IA? Tu opinión nos ayuda a mejorar continuamente.",
              "Què et sembla ESSENC·IA? La teva opinió ens ajuda a millorar contínuament."
            )}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <textarea
              data-testid="feedback-input"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t(
                "Comparte tu experiencia, sugerencias o lo que quieras decirnos...",
                "Comparteix la teva experiència, suggeriments o el que vulguis dir-nos..."
              )}
              rows={4}
              className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 resize-none transition-all text-sm"
              required
            />
            <button
              data-testid="submit-feedback-btn"
              type="submit"
              disabled={loading || !feedback.trim()}
              className="w-full bg-gradient-to-r from-teal-400 to-teal-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? t("Enviando...", "Enviant...") : t("Enviar Feedback", "Enviar Feedback")}</span>
            </button>
          </form>

          <p className="text-xs text-gray-500 mt-3 text-center">
            {t("Leemos cada mensaje con atención 💛", "Llegim cada missatge amb atenció 💛")}
          </p>
          <p className="text-[10px] text-gray-600 mt-2 text-center leading-relaxed">
            {t(
              "Al enviar aceptas nuestra ",
              "En enviar acceptes la nostra "
            )}
            <a
              href="/legal?section=privacy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="feedback-privacy-link"
              className="text-teal-400 hover:text-teal-300 underline"
            >
              {t("Política de Privacidad", "Política de Privacitat")}
            </a>
            .
          </p>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
